/**
 * The plans as cards: Team; Business, its people chosen with a select; Enterprise, by talking to us; and what every
 * plan includes. One layout for the landing site's pricing (rendered to static HTML there, its script switching the
 * period, currency and Business' people by the `data-` attributes) and the app's Plan page (live, priced by the
 * server): each fills in the prices and the actions, the cards and their words are the same.
 */
import type { ComponentChildren } from "preact";
import { CONTACT, type Period, type PlanCard, type PricingCopy, planFeatures, TEAM_MEMBERS } from "../lib/pricing.ts";
import { Select } from "./form.tsx";
import { CheckIcon } from "./icons.tsx";
import { Badge } from "./misc.tsx";
import { Segmented } from "./Segmented.tsx";
import { buttonClass } from "./styles.ts";

const PLAN = "flex flex-col rounded-lg border bg-white p-6";

function Checks({ items, cols = "flex flex-col" }: { items: readonly string[]; cols?: string }) {
  return (
    <ul class={`${cols} gap-2 text-sm text-zinc-700`}>
      {items.map((i, n) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a fixed list; the site's script fills in each by its place.
        <li key={n} class="flex gap-2">
          <CheckIcon size={16} class="mt-0.5 shrink-0 text-blue-600" />
          <span data-feature={true}>{i}</span>
        </li>
      ))}
    </ul>
  );
}

/** A card's features, under a rule (with what they add to, when they do). */
function Plus({ label, items }: { label?: string | undefined; items: readonly string[] }) {
  return (
    <div class="mt-6 border-t border-zinc-100 pt-5">
      {label ? <p class="mb-3 text-xs font-medium text-zinc-500">{label}</p> : null}
      <Checks items={items} />
    </div>
  );
}

/** A plan's price: the month's (a year's twelfth, yearly) large, how it's billed below. `data`: the site's. */
export function PlanPrice(p: {
  copy: PricingCopy;
  amount: string;
  billed: string;
  /** The site's script: each currency's monthly prices (`data-price`), and both billing lines. */
  data?: string;
}) {
  return (
    <>
      <p class="mt-5 flex items-baseline gap-1.5" data-price={p.data}>
        <span data-amount={true} class="text-4xl font-semibold tracking-tight text-zinc-900 tabular-nums">
          {p.amount}
        </span>
        <span class="text-sm text-zinc-500">{p.copy.perMonth}</span>
      </p>
      <p
        data-billed={p.data === undefined ? undefined : true}
        data-monthly={p.data === undefined ? undefined : p.copy.billedMonthly}
        data-yearly={p.data === undefined ? undefined : p.copy.billedYearly}
        class="mt-1 text-xs text-zinc-500"
      >
        {p.billed}
      </p>
    </>
  );
}

export interface PlanCardsProps {
  copy: PricingCopy;
  period: Period;
  onPeriod: (p: Period) => void;
  /** Business' people (one of `options`), and choosing them. */
  members: string;
  onMembers: (m: string) => void;
  options: readonly string[];
  /** Each card's price (`PlanPrice`, or what stands for it). */
  price: (plan: PlanCard) => ComponentChildren;
  /** Each card's action (a full-width button or link), and a short line under it. */
  action: (plan: PlanCard) => ComponentChildren;
  note: (plan: PlanCard) => ComponentChildren;
  /** Beside the period: the site's currency select. */
  controls?: ComponentChildren;
  /** The site's script: Business' prices by its people (`data-prices`), and its features by them (`data-features`). */
  data?: { prices: string; features: string };
  /** Hides the controls until the site's script runs (it does what they do). */
  controlsClass?: string;
}

/** The cards. */
export function PlanCards(p: PlanCardsProps) {
  const c = p.copy;
  const members = Number(p.members);
  return (
    <>
      <div
        data-controls={true}
        class={`mb-6 flex flex-wrap items-center justify-between gap-3 ${p.controlsClass ?? ""}`}
      >
        <div data-seg="period" data-values="monthly yearly">
          <Segmented
            label={c.period}
            value={p.period}
            onValue={p.onPeriod}
            options={[
              { value: "monthly", label: c.monthly },
              {
                value: "yearly",
                label: (
                  <>
                    {c.yearly}
                    <span class="text-xs font-medium text-blue-700">{c.free}</span>
                  </>
                ),
              },
            ]}
          />
        </div>
        {p.controls}
      </div>
      <ul class="grid gap-4 lg:grid-cols-3">
        <li class={`${PLAN} border-zinc-200`}>
          <h3 class="text-lg font-semibold text-zinc-900">{c.team.name}</h3>
          <p class="mt-1 text-sm text-zinc-500">{c.team.members}</p>
          {p.price("team")}
          <div class="mt-6 flex flex-col">{p.action("team")}</div>
          <div class="mt-2 text-center text-xs text-zinc-500">{p.note("team")}</div>
          <Plus items={planFeatures(c.team.features, TEAM_MEMBERS)} />
        </li>
        <li class={`${PLAN} border-blue-600 ring-1 ring-blue-600`} data-features={p.data?.features}>
          <h3 class="flex items-center gap-2 text-lg font-semibold text-zinc-900">
            {c.business.name}
            <Badge tone="blue">{c.recommended}</Badge>
          </h3>
          <div class={`mt-1 ${p.controlsClass ?? ""}`}>
            <Select
              aria-label={c.business.membersLabel}
              data-members={p.data ? true : undefined}
              data-prices={p.data?.prices}
              value={p.members}
              onValue={p.onMembers}
              options={p.options.map((n) => ({ value: n, label: c.business.membersOption.replace("{n}", n) }))}
            />
          </div>
          {p.data ? (
            <p class="mt-1 hidden text-sm text-zinc-500 no-js:block">
              {c.business.membersOption.replace("{n}", p.members)}
            </p>
          ) : null}
          {p.price("business")}
          <div class="mt-6 flex flex-col">{p.action("business")}</div>
          <div class="mt-2 text-center text-xs text-zinc-500">{p.note("business")}</div>
          <Plus label={c.business.plus} items={planFeatures(c.business.features, members)} />
        </li>
        <li class={`${PLAN} border-zinc-200`}>
          <h3 class="text-lg font-semibold text-zinc-900">{c.enterprise.name}</h3>
          <p class="mt-1 text-sm text-zinc-500">{c.enterprise.members}</p>
          <p class="mt-5 text-sm text-zinc-700">{c.enterprise.body}</p>
          <a href={`mailto:${CONTACT}`} class={`${buttonClass("secondary", "md")} mt-6 w-full`}>
            {c.enterprise.cta}
          </a>
          <p class="mt-2 text-center text-xs text-zinc-500">
            {c.enterprise.or} <span class="font-medium text-zinc-900 select-all">{CONTACT}</span>
          </p>
          <Plus label={c.enterprise.plus} items={c.enterprise.features} />
        </li>
      </ul>
      <div class="mt-4 rounded-lg border border-zinc-200 bg-white p-6">
        <h3 class="text-sm font-semibold text-zinc-900">{c.includesTitle}</h3>
        <div class="mt-4">
          <Checks items={c.includes} cols="grid gap-x-8 sm:grid-cols-2 lg:grid-cols-3" />
        </div>
        <p class="mt-4 text-xs text-zinc-500">{c.membersNote}</p>
      </div>
    </>
  );
}
